<template>
    <div class="rd">
        <div class="pagina">
            <header class="paginakop">
                <div class="tekst">
                    <h1>Tools &amp; downloads</h1>
                    <p>Exports for your administration, and help for operators who cannot log in.</p>
                </div>
            </header>

            <template v-if="isManagementUser">
                <section class="rij rij-3">
                    <div class="paneel tool">
                        <div class="paneel-kop"><h2>Customer data</h2></div>
                        <p class="muted">All customers who booked, with email, team name and number of visits. Use it for your newsletter, only for customers who agreed.</p>
                        <div class="knoprij">
                            <button class="btn btn-ghost" type="button" :disabled="busy.customers" @click="downloadCSV">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4v11M7 10l5 5 5-5M5 20h14" /></svg>
                                Download CSV
                            </button>
                        </div>
                    </div>
                    <div class="paneel tool">
                        <div class="paneel-kop"><h2>Invoice data per month</h2></div>
                        <p class="muted">Bookings per location with player counts and amounts, for the monthly invoice.</p>
                        <div class="veld">
                            <label for="t-maand">Month</label>
                            <select id="t-maand" v-model="selectedMonthlyPeriod">
                                <option v-for="p in invoicePeriodsMonthly" :key="p.id" :value="p.id">{{ p.label }}</option>
                            </select>
                        </div>
                        <div class="knoprij">
                            <button class="btn btn-ghost" type="button" :disabled="busy.monthly" @click="downloadMonthlyInvoicingCSV">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4v11M7 10l5 5 5-5M5 20h14" /></svg>
                                Download CSV
                            </button>
                        </div>
                    </div>
                    <div class="paneel tool">
                        <div class="paneel-kop"><h2>Invoice data per quarter</h2></div>
                        <p class="muted">The same data, combined per quarter for your accountant.</p>
                        <div class="veld">
                            <label for="t-kwart">Quarter</label>
                            <select id="t-kwart" v-model="selectedQuarterlyPeriod">
                                <option v-for="p in invoicePeriodsQuarterly" :key="p.id" :value="p.id">{{ p.label }}</option>
                            </select>
                        </div>
                        <div class="knoprij">
                            <button class="btn btn-ghost" type="button" :disabled="busy.quarterly" @click="downloadQuarterlyInvoicingCSV">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4v11M7 10l5 5 5-5M5 20h14" /></svg>
                                Download CSV
                            </button>
                        </div>
                    </div>
                </section>

                <section class="paneel" style="max-width: 720px">
                    <div class="paneel-kop">
                        <div class="tekst">
                            <h2>Login link for a user</h2>
                            <span class="uitleg">You get an email with a link that logs you in as this user. Use it to see what they see when they report a problem.</span>
                        </div>
                    </div>
                    <form id="t-form" class="filter-rij" novalidate @submit.prevent="sendLoginLink">
                        <div class="veld" style="flex: 1 1 280px">
                            <label for="t-mail">User email</label>
                            <input id="t-mail" v-model="userEmail" type="email" placeholder="operator@example.com" autocomplete="off" :aria-invalid="!!emailError" />
                            <span class="fout">{{ emailError }}</span>
                        </div>
                        <button class="btn btn-paars" type="submit" style="margin-bottom: 23px" :disabled="busy.login">Email me the link</button>
                    </form>
                </section>
            </template>

            <div v-else class="leeg">
                <h2>Not available</h2>
                <p>Tools and downloads are for X-Cube staff.</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { Message } from "element-ui";
import { computed, reactive, ref } from "vue";
import { getCustomersCSVJSON, getInvoicingCSV, requestLoginLinkOnServer } from "../../api/rest";
import { isManagement } from "../../helpers/auth";
import { saveTextAsFile } from "../../helpers/saveTextasFile";
import useCurrentUserStore from "../../store/userStore";

dayjs.extend(quarterOfYear);
const store = useCurrentUserStore();

// Default to the most recent period so the native select is never blank.
const selectedMonthlyPeriod = ref(1);
const selectedQuarterlyPeriod = ref(1);
const userEmail = ref("");
const emailError = ref("");
const busy = reactive({ customers: false, monthly: false, quarterly: false, login: false });

const isManagementUser = computed(() => isManagement(store.currentUser));

const invoicePeriodsMonthly = computed(() => {
    const periods = [];
    for (let i = 1; i < 12; i++) {
        const month = dayjs().subtract(i, "month");
        periods.push({ id: i, label: month.format("MMMM YYYY"), year: month.year(), month: month.month() + 1 });
    }
    return periods;
});

const invoicePeriodsQuarterly = computed(() => {
    const periods = [];
    for (let i = 1; i < 4; i++) {
        const quarter = dayjs().subtract(i, "quarter");
        periods.push({ id: i, label: `Q${quarter.quarter()} ${quarter.year()}`, year: quarter.year(), quarter: quarter.quarter() });
    }
    return periods;
});

const fileName = (label) => label.toLowerCase().replace(/\s+/g, "_");

const downloadCSV = async () => {
    busy.customers = true;
    try {
        const result = await getCustomersCSVJSON();
        saveTextAsFile(result.csvData, "customer_data.csv", "csv");
        Message.success("Customer data downloaded");
    } catch (error) {
        Message.error(`Download failed: ${error.message}`);
    } finally {
        busy.customers = false;
    }
};

const downloadMonthlyInvoicingCSV = async () => {
    const period = invoicePeriodsMonthly.value.find((p) => p.id === selectedMonthlyPeriod.value);
    if (!period) {
        Message.error("Pick a month first");
        return;
    }
    busy.monthly = true;
    try {
        const result = await getInvoicingCSV(period.year, period.month);
        saveTextAsFile(result, `invoicing_data_${fileName(period.label)}.csv`, "csv");
        Message.success(`Invoice data for ${period.label} downloaded`);
    } catch (error) {
        Message.error(`Download failed: ${error.message}`);
    } finally {
        busy.monthly = false;
    }
};

const downloadQuarterlyInvoicingCSV = async () => {
    const period = invoicePeriodsQuarterly.value.find((p) => p.id === selectedQuarterlyPeriod.value);
    if (!period) {
        Message.error("Pick a quarter first");
        return;
    }
    busy.quarterly = true;
    try {
        const result = await getInvoicingCSV(period.year, period.quarter);
        saveTextAsFile(result, `invoicing_data_${fileName(period.label)}.csv`, "csv");
        Message.success(`Invoice data for ${period.label} downloaded`);
    } catch (error) {
        Message.error(`Download failed: ${error.message}`);
    } finally {
        busy.quarterly = false;
    }
};

const sendLoginLink = async () => {
    const value = userEmail.value.trim();
    if (!value) {
        emailError.value = "Enter the email address the user logs in with.";
        return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
        emailError.value = "This email address is missing an @ or a domain.";
        return;
    }
    emailError.value = "";
    busy.login = true;
    try {
        await requestLoginLinkOnServer(value);
        Message.success("Login link will be sent if the email exists");
        userEmail.value = "";
    } catch (error) {
        Message.error(`Error from server: ${error.message}`);
    } finally {
        busy.login = false;
    }
};
</script>
